<template>
  <div
    v-if="availableOptions.length"
    class="bk-blokkli-item-options"
    @pointerup="onPointerUp"
    @mouseleave="onMouseLeave"
    @mouseenter="onMouseEnter"
  >
    <OptionsFormItem
      v-for="plugin in singleVisibleOptions"
      :key="plugin.property"
      :option="plugin.option"
      :property="plugin.property"
      :mutated-value="currentValues[plugin.property]"
      :uuids="uuids"
      class="bk-blokkli-item-options-item"
      :class="{
  'bk-is-disabled': isDisabled(plugin)
}"
      @keydown.stop
      @update="setOptionValue(plugin.property, $event)"
    />

    <OptionsFormGroup
      v-for="group in optionGroups"
      :key="'group_' + group.label"
      :label="group.label"
      :is-active="group.label === activeGroup"
      @toggle="onToggleGroup(group.label)"
    >
      <OptionsFormItem
        v-for="plugin in group.options"
        :key="plugin.property"
        :option="plugin.option"
        :property="plugin.property"
        :mutated-value="currentValues[plugin.property]"
        :uuids="uuids"
        class="bk-blokkli-item-options-item"
        :class="{
  'bk-is-disabled': isDisabled(plugin)
}"
        is-grouped
        @keydown.stop
        @update="setOptionValue(plugin.property, $event)"
      />
    </OptionsFormGroup>
  </div>
</template>

<script setup>
import { ref, computed, useBlokkli, onBeforeUnmount, onMounted } from "#imports";
import { falsy, onlyUnique } from "#blokkli/helpers";
import OptionsFormItem from "./Item.vue";
import OptionsFormGroup from "./Group.vue";
import { getRuntimeOptionValue } from "#blokkli/runtime-helpers";
import {
  BK_HIDDEN_GLOBALLY,
  BK_VISIBLE_LANGUAGES
} from "#blokkli/helpers/symbols";
if (import.meta.hot) {
  import.meta.hot.accept("#blokkli/runtime-helpers", () => {
  });
}
const activeGroup = ref("");
function optionValueToStorable(definition, value) {
  if (definition.type === "checkbox") {
    if (typeof value === "string" && (value === "1" || value === "0")) {
      return value;
    } else if (typeof value === "boolean") {
      return value === true ? "1" : "0";
    }
    return "0";
  } else if (definition.type === "text" || definition.type === "radios" || definition.type === "datetime-local") {
    if (typeof value === "string") {
      return value;
    }
  } else if (definition.type === "checkboxes") {
    if (Array.isArray(value)) {
      return value.join(",");
    } else if (typeof value === "string") {
      return value;
    }
  }
  return "";
}
function onToggleGroup(label) {
  if (activeGroup.value === label) {
    activeGroup.value = "";
  } else {
    activeGroup.value = label;
  }
}
const {
  adapter,
  eventBus,
  state,
  selection,
  runtimeConfig,
  dom,
  theme,
  context,
  definitions
} = useBlokkli();
const props = defineProps({
  uuids: { type: Array, required: true },
  definition: { type: null, required: true }
});
let pointerTimeout = null;
let mouseLeaveTimeout = null;
function onMouseLeave() {
  onMouseEnter();
  mouseLeaveTimeout = window.setTimeout(stopChangingOptions, 500);
}
function onMouseEnter() {
  if (mouseLeaveTimeout) {
    window.clearTimeout(mouseLeaveTimeout);
  }
}
function onPointerUp(e) {
  if (pointerTimeout) {
    clearTimeout(pointerTimeout);
  }
  selection.isChangingOptions.value = true;
  if (e.pointerType === "touch") {
    pointerTimeout = window.setTimeout(() => {
      stopChangingOptions();
    }, 2e3);
  }
}
function stopChangingOptions() {
  if (pointerTimeout) {
    clearTimeout(pointerTimeout);
  }
  if (!selection.isChangingOptions.value) {
    return;
  }
  props.uuids.forEach((uuid) => {
    dom.refreshBlockRect(uuid);
    const block = dom.findBlock(uuid);
    if (block) {
      const el = dom.getDragElement(block);
      if (el) {
        theme.invalidateCachedStyle(el);
      }
    }
  });
  selection.isChangingOptions.value = false;
}
class OptionCollector {
  options;
  constructor() {
    this.options = {};
  }
  set(uuid, key, value) {
    if (!this.options[uuid]) {
      this.options[uuid] = {};
    }
    this.options[uuid][key] = value;
  }
  get(uuid, key) {
    return this.options[uuid]?.[key];
  }
  getEntries() {
    return Object.entries(this.options).map(([uuid, options]) => {
      return Object.entries(options).map(([key, value]) => {
        return {
          uuid,
          key,
          value
        };
      });
    }).flat();
  }
}
const original = new OptionCollector();
const updated = new OptionCollector();
const availableOptions = computed(() => {
  if (!props.definition) {
    return [];
  }
  const options = props.definition.options || {};
  const global = (props.definition.globalOptions || []).reduce((acc, v) => {
    const globalDefinition = definitions.globalOptions.value[v] || null;
    if (globalDefinition) {
      acc[v] = globalDefinition;
    }
    return acc;
  }, {});
  return Object.entries({ ...options, ...global }).map(([property, option]) => {
    return {
      property,
      option
    };
  });
});
function getOptionValue(uuid, key, defaultValue) {
  if (!uuid) {
    return "";
  }
  const blockMutatedOptions = state.mutatedOptions[uuid];
  if (blockMutatedOptions !== void 0 && blockMutatedOptions[key] !== void 0) {
    return blockMutatedOptions[key];
  }
  return defaultValue;
}
const currentValues = computed(() => {
  return availableOptions.value.reduce((acc, v) => {
    const values = props.uuids.map((uuid) => {
      return JSON.stringify(
        getRuntimeOptionValue(
          v.option,
          getOptionValue(uuid, v.property, v.option.default)
        )
      );
    }).filter(onlyUnique);
    if (values.length === 1) {
      acc[v.property] = getRuntimeOptionValue(
        v.option,
        getOptionValue(props.uuids[0], v.property, v.option.default)
      );
    } else {
      acc[v.property] = "";
    }
    return acc;
  }, {});
});
function filterInternal(item) {
  if (currentValues.value[BK_HIDDEN_GLOBALLY]) {
    return item.property !== BK_VISIBLE_LANGUAGES;
  }
  return true;
}
function isInternalOption(property) {
  return property === BK_VISIBLE_LANGUAGES || property === BK_HIDDEN_GLOBALLY;
}
const visibleOptions = computed(() => {
  if (!props.definition.editor?.determineVisibleOptions) {
    return availableOptions.value.filter(filterInternal);
  }
  const uuid = props.uuids[0];
  const item = state.getFieldListItem(uuid);
  const block = selection.blocks.value.find((v) => v.uuid === uuid);
  if (!item) {
    return [];
  }
  const parentType = block?.hostType === runtimeConfig.itemEntityType ? block.parentBlockBundle : void 0;
  const ctxProps = item?.bundle === "from_library" ? item?.props?.libraryItem?.block?.props : item?.props;
  const visibleKeys = (
    // We have to cast to any here because the types are guaranteed to be correct.
    props.definition.editor.determineVisibleOptions({
      options: currentValues.value,
      parentType,
      props: ctxProps,
      entity: context.value,
      fieldListType: block?.hostFieldListType || "default"
    })
  );
  return availableOptions.value.filter(
    (v) => isInternalOption(v.property) || visibleKeys.includes(v.property)
  ).filter(filterInternal);
});
function isDisabled(plugin) {
  if (!state.canEdit.value || state.editMode.value === "readonly") {
    return true;
  }
  if (isInternalOption(plugin.property)) {
    return false;
  }
  return state.editMode.value !== "editing";
}
const singleVisibleOptions = computed(
  () => visibleOptions.value.filter((v) => !v.option.group)
);
const optionGroups = computed(() => {
  return Object.values(
    visibleOptions.value.reduce((acc, option) => {
      if (option.option.group) {
        if (!acc[option.option.group]) {
          acc[option.option.group] = {
            label: option.option.group,
            options: [option]
          };
        } else {
          acc[option.option.group].options.push(option);
        }
      }
      return acc;
    }, {})
  );
});
function setOptionValue(key, value) {
  props.uuids.forEach((uuid) => {
    updated.set(uuid, key, value);
    if (!state.mutatedOptions[uuid]) {
      state.mutatedOptions[uuid] = {};
    }
    state.mutatedOptions[uuid][key] = value;
    eventBus.emit("option:update", { uuid, key, value });
  });
}
onMounted(() => {
  props.uuids.forEach((uuid) => {
    availableOptions.value.forEach((option) => {
      const currentValue = getOptionValue(
        uuid,
        option.property,
        option.option.default
      );
      original.set(
        uuid,
        option.property,
        optionValueToStorable(option.option, currentValue)
      );
    });
  });
});
onBeforeUnmount(() => {
  selection.isChangingOptions.value = false;
  const values = updated.getEntries().map((entry) => {
    const originalValue = original.get(entry.uuid, entry.key);
    if (originalValue === entry.value) {
      return;
    }
    return entry;
  }).filter(falsy);
  if (!values.length) {
    return;
  }
  state.mutateWithLoadingState(() => adapter.updateOptions(values));
});
</script>

<script>
export default {
  name: "OptionsForm"
};
</script>
